import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Search, Star, TrendingUp, Clock, MapPin, Briefcase } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NetworkingPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { isDark } = useTheme();
  const [profiles, setProfiles] = useState([]);
  const [featuredCreators, setFeaturedCreators] = useState([]);
  const [newCreators, setNewCreators] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchProfiles();
    fetchFeaturedCreators();
    fetchNewCreators();
  }, []);

  const fetchProfiles = async (search = '') => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/networking/profiles`, {
        params: { search, limit: 50 },
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setProfiles(response.data.profiles || []);
    } catch (error) {
      console.error('Error fetching profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeaturedCreators = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/networking/featured`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setFeaturedCreators(response.data.featured_creators || []);
    } catch (error) {
      console.error('Error fetching featured creators:', error);
    }
  };

  const fetchNewCreators = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/networking/new-creators`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setNewCreators(response.data.new_creators || []);
    } catch (error) {
      console.error('Error fetching new creators:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProfiles(searchQuery);
  };

  const viewProfile = (userId) => {
    navigate(`/profile/${userId}`);
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: { color: 'bg-red-500', label: 'Admin' },
      artist: { color: 'bg-purple-500', label: 'Artist' },
      moderator: { color: 'bg-blue-500', label: 'Moderator' },
      user: { color: 'bg-gray-500', label: 'User' }
    };
    return roleConfig[role] || roleConfig.user;
  };

  const ProfileCard = ({ user }) => {
    const roleBadge = getRoleBadge(user.role);
    
    return (
      <Card 
        className={`${isDark ? 'bg-gray-800/50 border-gray-700 hover:border-yellow-500/50' : 'bg-white border-gray-200 hover:border-yellow-500'} transition-all cursor-pointer hover:scale-105`}
        onClick={() => viewProfile(user.id)}
      >
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            {/* Avatar */}
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-yellow-400 to-purple-500 flex items-center justify-center flex-shrink-0">
              {user.profile_picture ? (
                <img 
                  src={user.profile_picture} 
                  alt={user.username}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <Users className="w-8 h-8 text-white" />
              )}
            </div>

            {/* Profile Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1">
                  <h3 className={`text-lg font-bold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {user.display_name || user.username}
                  </h3>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>@{user.username}</p>
                </div>
                <Badge className={`${roleBadge.color} text-white text-xs`}>
                  {roleBadge.label}
                </Badge>
              </div>

              {/* Bio */}
              {user.bio && (
                <p className={`text-sm mt-2 line-clamp-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  {user.bio}
                </p>
              )}

              {/* Location */}
              {user.location && (
                <div className={`flex items-center gap-1 mt-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <MapPin className="w-3 h-3" />
                  <span>{user.location}</span>
                </div>
              )}

              {/* Stats */}
              <div className={`flex items-center gap-4 mt-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <div className="flex items-center gap-1">
                  <Briefcase className="w-3 h-3" />
                  <span>{user.content_count || 0} posts</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  <span>Joined {new Date(user.created_at).toLocaleDateString()}</span>
                </div>
              </div>

              {/* View Profile Button */}
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  viewProfile(user.id);
                }}
                className="mt-4 w-full bg-gradient-to-r from-yellow-400 to-purple-500 hover:from-yellow-500 hover:to-purple-600 text-black font-semibold"
                size="sm"
              >
                View Profile
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gradient-to-b from-black via-gray-900 to-black' : 'bg-gradient-to-b from-gray-50 via-white to-gray-100'}`}>
      {/* Header */}
      <div className={`backdrop-blur-xl border-b sticky top-0 z-40 ${isDark ? 'bg-black/80 border-gray-800' : 'bg-white/80 border-gray-200'}`}>
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl font-black bg-gradient-to-r from-yellow-400 to-purple-500 bg-clip-text text-transparent">
                Networking
              </h1>
              <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Connect with creators and discover talent</p>
            </div>
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="relative">
            <Search className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
            <Input
              type="text"
              placeholder="Search by name, username, or bio..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`pl-12 h-12 text-base ${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
            />
            <Button
              type="submit"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-yellow-400 to-purple-500 hover:from-yellow-500 hover:to-purple-600 text-black font-semibold"
              size="sm"
            >
              Search
            </Button>
          </form>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`border mb-8 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'}`}>
            <TabsTrigger 
              value="all"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-yellow-400 data-[state=active]:to-purple-500 data-[state=active]:text-black"
            >
              <Users className="w-4 h-4 mr-2" />
              All Creators ({profiles.length})
            </TabsTrigger>
            <TabsTrigger 
              value="featured"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-yellow-400 data-[state=active]:to-purple-500 data-[state=active]:text-black"
            >
              <Star className="w-4 h-4 mr-2" />
              Featured ({featuredCreators.length})
            </TabsTrigger>
            <TabsTrigger 
              value="new"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-yellow-400 data-[state=active]:to-purple-500 data-[state=active]:text-black"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              New Creators ({newCreators.length})
            </TabsTrigger>
          </TabsList>

          {/* All Creators Tab */}
          <TabsContent value="all">
            {loading ? (
              <div className={`text-center py-20 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading profiles...</div>
            ) : profiles.length === 0 ? (
              <div className="text-center py-20">
                <Users className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>No profiles found</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {profiles.map((user) => (
                  <ProfileCard key={user.id} user={user} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Featured Tab */}
          <TabsContent value="featured">
            {featuredCreators.length === 0 ? (
              <div className="text-center py-20">
                <Star className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>No featured creators yet</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredCreators.map((user) => (
                  <ProfileCard key={user.id} user={user} />
                ))}
              </div>
            )}
          </TabsContent>

          {/* New Creators Tab */}
          <TabsContent value="new">
            {newCreators.length === 0 ? (
              <div className="text-center py-20">
                <TrendingUp className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                <p className={isDark ? 'text-gray-400' : 'text-gray-500'}>No new creators</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {newCreators.map((user) => (
                  <ProfileCard key={user.id} user={user} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default NetworkingPage;